import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Box,
  Typography,
  Paper,
  Container,
  Alert,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BranchSelectionDialog from './BranchSelectionDialog';

const Login = (): React.ReactElement => {
  const { login, isAuthenticated, error, loading, clearError, user, showBranchDialog, setActiveBranch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // مراجع للحقول
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // مراجع لتتبع القيم الحقيقية (لمنع التعبئة التلقائية)
  const usernameValueRef = useRef('');
  const passwordValueRef = useRef('');

  const savedPath = localStorage.getItem('redirect_after_login');
  const from = location.state?.from?.pathname || savedPath || '/dashboard';

  useEffect(() => {
    if (isAuthenticated && !showBranchDialog) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from, showBranchDialog]);

  useEffect(() => {
    return () => { clearError(); };
  }, []);

  // مراقبة التعبئة التلقائية ومنعها بشكل مستمر
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;

    const checkAndClearAutofill = () => {
      const uInput = usernameRef.current;
      const pInput = passwordRef.current;

      if (uInput && uInput.value !== usernameValueRef.current) {
        // المتصفح عبّأ الحقل تلقائياً - نمسحه
        uInput.value = usernameValueRef.current;
        // نستخدم nativeInputValueSetter لإجبار React على التحديث
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(uInput, usernameValueRef.current);
          uInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      if (pInput && pInput.value !== passwordValueRef.current) {
        pInput.value = passwordValueRef.current;
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        if (nativeInputValueSetter) {
          nativeInputValueSetter.call(pInput, passwordValueRef.current);
          pInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    };

    // فحص كل 100ms لمدة 3 ثوانٍ بعد التحميل
    intervalId = setInterval(checkAndClearAutofill, 100);
    const timeoutId = setTimeout(() => clearInterval(intervalId), 3000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  const handleUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    usernameValueRef.current = val;
    setUsername(val);
    if (usernameError) setUsernameError('');
  }, [usernameError]);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    passwordValueRef.current = val;
    setPassword(val);
    if (passwordError) setPasswordError('');
  }, [passwordError]);

  const handleBranchSelect = (branchId: string) => {
    setActiveBranch(branchId);
    navigate(from, { replace: true });
  };

  const validate = () => {
    let valid = true;
    if (!username.trim()) {
      setUsernameError('اسم المستخدم مطلوب');
      valid = false;
    } else {
      setUsernameError('');
    }
    if (!password.trim()) {
      setPasswordError('كلمة المرور مطلوبة');
      valid = false;
    } else {
      setPasswordError('');
    }
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await login(username, password);
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBaseStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 14px',
    fontSize: '16px',
    border: '1px solid rgba(0,0,0,0.23)',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    backgroundColor: '#fff',
    color: 'rgba(0,0,0,0.87)',
    direction: 'rtl'
  };

  const inputErrorStyle: React.CSSProperties = {
    ...inputBaseStyle,
    border: '1px solid #d32f2f'
  };

  return (
    <Container component="main" maxWidth="sm">
      {isAuthenticated && showBranchDialog && user?.accessibleBranches && user.accessibleBranches.length > 0 && (
        <BranchSelectionDialog
          open={true}
          branches={user.accessibleBranches}
          onSelect={handleBranchSelect}
          defaultBranch={user.defaultBranch?._id}
        />
      )}

      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2, border: '1px solid rgba(0,0,0,0.05)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', borderRadius: '50%', p: 1, mb: 2 }}>
              <LockOutlinedIcon />
            </Box>
            <Typography component="h1" variant="h5" fontWeight="bold">
              تسجيل الدخول
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
              {error}
            </Alert>
          )}

          {/*
            الحل النهائي لمنع التعبئة التلقائية:
            - الحقلان من نوع "text" وليس "password" أو "email"
            - كلمة المرور مخفية بـ CSS فقط (-webkit-text-security)
            - لا يوجد name أو id يشير إلى username/password
            - autoComplete="off" على الـ form وعلى كل حقل
          */}
          <form
            onSubmit={handleSubmit}
            autoComplete="off"
            noValidate
            style={{ marginTop: '8px' }}
          >
            {/* حقل اسم المستخدم */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: usernameError ? '#d32f2f' : 'rgba(0,0,0,0.6)',
                marginBottom: '6px',
                fontFamily: 'inherit'
              }}>
                اسم المستخدم *
              </label>
              <input
                ref={usernameRef}
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
                aria-label="اسم المستخدم"
                value={username}
                onChange={handleUsernameChange}
                disabled={loading || isSubmitting}
                style={usernameError ? inputErrorStyle : inputBaseStyle}
              />
              {usernameError && (
                <span style={{ fontSize: '12px', color: '#d32f2f', marginTop: '3px', display: 'block' }}>
                  {usernameError}
                </span>
              )}
            </div>

            {/* حقل كلمة المرور - نوعه text لكن مخفي بـ CSS */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '12px',
                color: passwordError ? '#d32f2f' : 'rgba(0,0,0,0.6)',
                marginBottom: '6px',
                fontFamily: 'inherit'
              }}>
                كلمة المرور *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={passwordRef}
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  aria-label="كلمة المرور"
                  value={password}
                  onChange={handlePasswordChange}
                  onPaste={(e) => e.preventDefault()}
                  disabled={loading || isSubmitting}
                  style={{
                    ...(passwordError ? inputErrorStyle : inputBaseStyle),
                    paddingLeft: '48px',
                    // إخفاء النص بـ CSS بدلاً من type="password"
                    ...(showPassword ? {} : { WebkitTextSecurity: 'disc' } as React.CSSProperties),
                  } as React.CSSProperties}
                />
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    left: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    padding: '4px'
                  }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </div>
              {passwordError && (
                <span style={{ fontSize: '12px', color: '#d32f2f', marginTop: '3px', display: 'block' }}>
                  {passwordError}
                </span>
              )}
            </div>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || isSubmitting}
            >
              {(loading || isSubmitting) ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'تسجيل الدخول'
              )}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
